import { supabase } from "./supabaseClient";
import { loadAppearances } from "./reportLoader";
import { findJobIdsForWebsite } from "./websiteSearch";

// The account's scans, newest first. The database rules already hide other
// accounts' scans; the filter keeps the list to this one account even for a
// login that belongs to several.
// Each comes with its niche name (niches.name) for the niche groups; if the
// names can't be joined, the list still loads without them.
export async function fetchJobs(agencyId) {
  if (!agencyId) return [];
  const list = columns => supabase
    .from("jobs")
    .select(columns)
    .eq("agency_id", agencyId)
    .order("started_at", { ascending: false });
  const { data, error } = await list("*, niches(name)");
  if (!error) return data || [];
  console.error("fetchJobs", error);
  const plain = await list("*");
  return plain.data || [];
}

// The ids of this account's scans that saw a website ("acme.co.uk").
export function fetchJobIdsForWebsite(agencyId, domain) {
  return findJobIdsForWebsite(supabase, agencyId, domain);
}

// One scan, or null if it doesn't exist or belongs to another account.
export async function fetchJob(jobId) {
  const { data } = await supabase.from("jobs").select("*").eq("id", jobId).maybeSingle();
  return data;
}

export async function createJob(fields) {
  const { data, error } = await supabase
    .from("jobs")
    .insert(fields)
    .select()
    .single();
  return { job: data, error };
}

// Also stops any samples still waiting, so they don't sit "pending", and
// marks searches not yet tried as skipped. The Worker never goes back to a
// stopped sample, so it can't do this itself.
export async function stopJob(jobId) {
  const now = new Date().toISOString();
  await supabase.from("jobs").update({ status: "stopped", completed_at: now }).eq("id", jobId);
  await supabase.from("samples").update({ status: "stopped" }).eq("job_id", jobId).in("status", ["pending", "running"]);
  await supabase.from("runs").update({ status: "stopped", finished_at: now }).eq("job_id", jobId).in("status", ["pending", "running"]);
  const { error } = await supabase.from("searches").update({ status: "skipped" }).eq("job_id", jobId).eq("status", "pending");
  if (error) console.error("stopJob: could not mark searches skipped", error);
}

// Every appearance saved for a job, fetched a page at a time.
export function fetchAppearances(jobId) {
  return loadAppearances(supabase, jobId);
}

// The latest few appearances, for the live preview while scanning.
export async function fetchRecentAppearances(jobId, limit) {
  const { data } = await supabase
    .from("appearances")
    .select("*")
    .eq("job_id", jobId)
    .order("created_at", { ascending: false })
    .limit(limit);
  return data || [];
}
